import { Fragment } from 'react'
import PropTypes from 'prop-types'
import InfiniteScroll from '../../infinite-scroll/InfiniteScroll'

import { ReactComponent as OutsideLinkIcon } from '../../../assets/images/common/icons/outside-link-icon.svg'
import StudentWithAvatar from '../../students/StudentWithAvatar'

import classes from './StudentsTable.module.scss'
import { calculateAge, genderLabel } from '../../../utility/userHelper'
import Loader from '../../common/loader/Loader'
import classNames from 'classnames'
import moment from 'moment'

import colors from '../../../assets/styles/globals.scss'



const StudentsTable = ({ students, fetchData, pagination, onStudentClick, inspectedStudentId, isExporting, isLoading }) => {
  const hasMore = !pagination?.totalPages || (pagination?.page + 1) <= pagination?.totalPages

  if (!students || !pagination) {
    return (
      <div className='h-100 d-flex align-items-center justify-content-center'>
        <Loader>Loading Students</Loader>
      </div>
    )
  }

  return (
    <div id='studentsTable' className={classes.table}>
      <InfiniteScroll
        dataLength={students.length}
        next={fetchData}
        hasMore={hasMore}
        scrollableTarget='tableContent'
        className={classes.tableContent}
        hideLoader
      >
        <div className={classes.gridTable} id='tableContent'>
          <div className={classNames(classes.col, classes.header, classes.firstColumn, classes.topLeft)}>
            <span className='font-weight-bold'>Students ({pagination?.totalCount})</span>
          </div>
          <div className={classNames(classes.col, classes.header)}>
            <span>Years in Community</span>
          </div>
          <div className={classNames(classes.col, classes.header)}>
            <span>Enrollment Status</span>
          </div>
          <div className={classNames(classes.col, classes.header)}>
            <span>Total Enrollments</span>
          </div>
          <div className={classNames(classes.col, classes.header)}>
            <span>Total Revenue</span>
          </div>
          <div className={classNames(classes.col, classes.header)}>
            <span>Strokes Lost</span>
          </div>
          <div className={classNames(classes.col, classes.header)}>
            <span>9HE Rounds</span>
          </div>
          <div className={classNames(classes.col, classes.header)}>
            <span>Casual Rounds</span>
          </div>
          <div className={classNames(classes.col, classes.header)}>
            <span>Practice Sessions</span>
          </div>
          <div className={classNames(classes.col, classes.header)}>
            <span>Time Practiced</span>
          </div>
          <div className={classNames(classes.col, classes.header)}>
            <span>Age</span>
          </div>
          <div className={classNames(classes.col, classes.header)}>
            <span>Gender</span>
          </div>
          <div className={classNames(classes.col, classes.header)}>
            <span>Level</span>
          </div>

          {isExporting ? (
            <div className={classes.exportLoadingWrapper}>
              <Loader loaderType='ball-beat' loaderColor={colors.lightNavy}>Exporting Members</Loader>
              <span className='text-center'>Depending on community size this may take several minutes.</span>
            </div>
          ) : (
            <>
              {students.map(student => (
                <div
                  tabIndex={0}
                  key={student.id}
                  role='button'
                  className={classNames(classes.row, { [classes.isClicked]: student.id === inspectedStudentId })}
                  onClick={() => onStudentClick(student)}
                  onKeyDown={() => null}
                >
                  <div
                    className={classNames(classes.col, classes.firstColumn, classes.studentInfo)}
                    title={`${student.firstName} ${student.lastName}`}
                  >
                    <StudentWithAvatar student={student} avatarClassName='small-round-image-wrapper' />
                    <div className='d-flex flex-column flex-grow-1 pr-3'>
                      <h6 className='mb-1 font-weight-bold'>{student.firstName} {student.lastName}</h6>
                      <span>
                        {student.birthdate !== null && `${calculateAge(student.birthdate)}yrs / `}
                        {genderLabel[student.gender]}
                      </span>
                    </div>
                    <OutsideLinkIcon className={classes.detailsIcon} />
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol, { [classes.isClicked]: student.id === inspectedStudentId })}>
                    <span className='font-weight-bold'>{student.firstActivity ? moment(student.firstActivity).fromNow(true) : '-'}</span>
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <span className={classNames({ [classes.enrollmentActive]: !!student.activeCourseId })}>
                      {student.activeCourseId ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <span className='font-weight-bold'>{student.enrollments?.length || 0}</span>
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <span className='font-weight-bold'>${student.totalRevenue}</span>
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <span className='font-weight-bold'>{student.strokesLost}</span>
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <span className='font-weight-bold'>{student.nineHoleRounds}</span>
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <span className='font-weight-bold'>{student.casualRounds}</span>
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <span className='font-weight-bold'>{student.practiceSessions}</span>
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <span className='font-weight-bold'>{student.timePracticed} hr</span>
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <span className='font-weight-bold'>{student.age}</span>
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <span className='font-weight-bold'>{student.genderLabel}</span>
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <span className='font-weight-bold'>{student.currentFormalDivision}</span>
                  </div>
                </div>
              ))}

              {!!(hasMore && isLoading) && Array.from({ length: 3 }, (_, idx) => (
                <div className={classNames(classes.row, classes.skeletons)} key={`skeleton-${idx}`}>
                  <div
                    tabIndex={0}
                    role='button'
                    onKeyDown={() => null}
                    className={
                      classNames(classes.col, classes.firstColumn, classes.studentInfo)
                    }
                  >
                    <div data-skeleton='avatar' />
                    <div className={classNames('d-flex flex-column flex-grow-1 pr-3', classes.skeletonInfoWrapper)}>
                      <div data-skeleton='name' />
                      <div data-skeleton='info' />
                    </div>
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <div data-skeleton='longValue' />
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <div data-skeleton='longValue' />
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <div data-skeleton='value' />
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <div data-skeleton='longValue' />
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <div data-skeleton='value' />
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <div data-skeleton='value' />
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <div data-skeleton='value' />
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <div data-skeleton='value' />
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <div data-skeleton='longValue' />
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <div data-skeleton='value' />
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <div data-skeleton='longValue' />
                  </div>

                  <div className={classNames(classes.col, classes.centeredCol)}>
                    <div data-skeleton='value' />
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </InfiniteScroll>
    </div>
  )
}

StudentsTable.propTypes = {
  students: PropTypes.array,
  fetchData: PropTypes.func.isRequired,
  onStudentClick: PropTypes.func,
  pagination: PropTypes.object,
  inspectedStudentId: PropTypes.number,
  isExporting: PropTypes.bool,
  isLoading: PropTypes.bool,
}

export default StudentsTable
